import { Alert, Button, Spinner, Textarea, cn } from '@blink/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, PackageX, Truck } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ErrorAlert, Screen } from '@/components/common';
import { aed } from '@/components/parcel-bits';
import { currentLocation } from '@/components/route-bits';
import { ManualAwb } from '@/components/manual-awb';
import { Scanner, type ScanSource } from '@/components/scanner';
import { loadTone, scanFeedback } from '@/components/scan-feedback';
import { useI18n } from '@/i18n';
import { api, type LoadScanAnswer, type LoadView, type RouteStartAnswer } from '@/lib/api';
import { useLiveUpdates } from '@/lib/live';
import { HelpTip } from '@/components/help-tip';

const TONE: Record<LoadScanAnswer['code'], 'success' | 'warning' | 'destructive'> = {
  LOADED: 'success',
  ALREADY_LOADED: 'warning',
  ALREADY_OUT: 'warning',
  NOT_YOURS: 'destructive',
  NOT_IN_HUB: 'destructive',
  SCAN_PIECE_LABELS: 'warning',
  UNKNOWN: 'destructive',
};

/** Screen 3: scan every piece into the vehicle, report missing pieces, start the route. */
export function Load() {
  const { t, f } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // an approval or a new parcel shows at once
  useLiveUpdates();
  const view = useQuery({ queryKey: ['courier', 'load'], queryFn: () => api<LoadView>('/courier/load') });
  const [last, setLast] = useState<LoadScanAnswer | null>(null);
  const [missingFor, setMissingFor] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [confirmStart, setConfirmStart] = useState(false);

  const scan = useMutation({
    mutationFn: (v: { barcode: string; source: ScanSource }) => api<LoadScanAnswer>('/courier/load/scan', { method: 'POST', body: v }),
    onSuccess: (answer) => {
      scanFeedback(loadTone(answer.code));
      setLast(answer);
      void queryClient.invalidateQueries({ queryKey: ['courier', 'load'] });
    },
    onError: () => scanFeedback('error'),
  });
  const missing = useMutation({
    mutationFn: (shipmentId: string) => api('/courier/load/missing', { method: 'POST', body: { shipmentId, note: note.trim() || null } }),
    onSuccess: () => {
      setMissingFor(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['courier'] });
    },
  });
  const start = useMutation({
    mutationFn: async () => api<RouteStartAnswer>('/courier/route/start', { method: 'POST', body: { location: await currentLocation() } }),
    onSuccess: (answer) => {
      void queryClient.invalidateQueries({ queryKey: ['courier'] });
      navigate(`/?started=${answer.out}&left=${answer.leftBehind.length}`, { replace: true });
    },
  });

  const v = view.data;
  const notReady = v ? v.parcels.length - v.ready : 0;

  return (
    <Screen
      title={<h1 className="flex items-center gap-1 text-lg font-semibold">{t.scan.title} <HelpTip id="courier.load" /></h1>}
      actions={
        <Button variant="ghost" size="icon" className="order-first text-white hover:bg-white/10 hover:text-white" aria-label={t.parcel.back} onClick={() => navigate('/')} data-testid="back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
      }
    >
      <ErrorAlert error={view.error ?? scan.error ?? start.error ?? missing.error} />
      {view.isLoading ? <Spinner /> : null}
      {v ? (
        <>
          <div className="space-y-1.5" data-testid="load-progress">
            <div className="flex justify-between text-sm">
              <span className="font-semibold">{f(t.scan.progress, { loaded: v.pieces.loaded, total: v.pieces.total })}</span>
              <span className="tabular-nums text-muted-foreground" data-testid="load-percent">{v.pieces.percent}%</span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-success transition-all" style={{ width: `${v.pieces.percent}%` }} />
            </div>
          </div>

          <Scanner busy={scan.isPending} onScan={(barcode, source) => scan.mutate({ barcode, source })} />

          {last ? (
            <Alert variant={TONE[last.code]} data-testid="scan-answer" data-code={last.code}>
              <p className="font-semibold">
                {last.awb ? <span className="font-mono">{last.awb}{last.piece && last.piece !== '1/1' ? ` · ${last.piece}` : ''} — </span> : <span className="font-mono">{last.barcode} — </span>}
                {t.scan.answer[last.code]}
              </p>
              {last.code === 'LOADED' && last.pieceCount && last.pieceCount > 1 ? (
                <p className="text-sm">{last.parcelLoaded ? t.scan.parcelComplete : f(t.scan.parcelProgress, { loaded: last.loadedCount ?? 0, total: last.pieceCount })}</p>
              ) : null}
            </Alert>
          ) : null}

          {v.parcels.length === 0 ? <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{t.scan.nothing}</p> : null}

          <ul className="space-y-2" data-testid="load-list">
            {v.parcels.map((p) => {
              const done = p.loadedCount === p.pieceCount;
              return (
                <li key={p.id} className={cn('rounded-xl border bg-card px-4 py-3', done && 'border-success/40 bg-success-soft/40')} data-testid="load-row" data-awb={p.awb} data-done={done}>
                  <div className="flex items-center gap-2">
                    {done ? <Check className="h-5 w-5 text-success" /> : null}
                    <span className="font-mono text-sm font-semibold">{p.awb}</span>
                    <span className="ms-auto text-sm tabular-nums text-muted-foreground">
                      {p.loadedCount}/{p.pieceCount}
                    </span>
                  </div>
                  {p.pieceCount > 1 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.pieces.map((pc) => (
                        <span key={pc.label} className={cn('rounded-md px-2 py-0.5 font-mono text-xs', pc.loaded ? 'bg-success text-white' : 'bg-muted text-muted-foreground')}>
                          {pc.label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {p.pieces.some((pc) => !pc.inHub) ? <p className="mt-1 text-xs text-destructive">{t.scan.notInHubHint}</p> : null}
                  {!done ? <ManualAwb shipmentId={p.id} pieces={p.pieces.filter((pc) => !pc.loaded).map((pc) => pc.label)} manual={p.manual} /> : null}
                  {!done ? (
                    p.missingReported ? (
                      <p className="mt-2 text-xs font-semibold text-warning-foreground" data-testid="missing-reported">{t.scan.missingReported}</p>
                    ) : missingFor === p.id ? (
                      <div className="mt-2 space-y-2">
                        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.scan.missingNote} rows={2} maxLength={300} />
                        <div className="flex gap-2">
                          <Button variant="destructive" className="flex-1" disabled={missing.isPending} onClick={() => missing.mutate(p.id)} data-testid="missing-confirm">
                            {t.scan.missingSend}
                          </Button>
                          <Button variant="outline" onClick={() => setMissingFor(null)}>
                            {t.scan.cancel}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="ghost" size="sm" className="mt-1 h-9 px-2 text-destructive" onClick={() => setMissingFor(p.id)} data-testid="report-missing">
                        <PackageX className="h-4 w-4" />
                        {t.scan.reportMissing}
                      </Button>
                    )
                  ) : null}
                </li>
              );
            })}
          </ul>

          {v.parcels.length ? (
            <div className="sticky bottom-0 -mx-4 space-y-2 border-t bg-background/95 p-4 backdrop-blur">
              {!v.routeStart.allowed ? (
                <Alert variant="destructive" data-testid="route-blocked">
                  <p>{f(t.cash.blocked, { amount: aed(v.routeStart.earlierFils) })}</p>
                  <Link to="/cash" className="mt-1 inline-block font-semibold underline">
                    {t.cash.openMyCash}
                  </Link>
                </Alert>
              ) : v.routeStart.allowedByHub ? (
                <Alert variant="warning" data-testid="route-allowed-by-hub">
                  {f(t.cash.allowedToday, { amount: aed(v.routeStart.earlierFils) })}
                </Alert>
              ) : null}
              {confirmStart && notReady > 0 ? (
                <Alert variant="warning" data-testid="start-warning">
                  {f(t.scan.leaveBehind, { count: notReady })}
                </Alert>
              ) : null}
              <Button
                className="h-14 w-full text-base"
                disabled={!v.ready || !v.routeStart.allowed || start.isPending}
                data-testid="start-route"
                onClick={() => (notReady > 0 && !confirmStart ? setConfirmStart(true) : start.mutate())}
              >
                <Truck className="h-5 w-5" />
                {confirmStart && notReady > 0 ? f(t.scan.startAnyway, { count: v.ready }) : f(t.scan.startRoute, { count: v.ready })}
              </Button>
              <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                What happens when I start? <HelpTip id="courier.start" />
              </p>
            </div>
          ) : null}
        </>
      ) : null}
    </Screen>
  );
}
